import {Injectable} from "@angular/core";
import {Observable, of, throwError} from "rxjs";
import {catchError, map} from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import {LoggerService} from '../../common/logger.service';
import {UsanaObject} from '../../usana-object';
import {UrlService} from '../../common/url.service';
import {IRestResponse} from '../../common/rest-response.model';
import {UsanaCookieService} from '../../common/usana-cookie.service';
import {Util} from "../../util/util";
import {Constants} from "../../constants";
import {PersonalAuthResponse} from "./personal-auth.model";

@Injectable({
    providedIn: 'root'
})
export class PersonalAuthService extends UsanaObject {

    private static PERSONAL_AUTH_COUNTRIES = [Constants.COUNTRY_KR];

    constructor(
        loggerService: LoggerService,
        private http: HttpClient,
        private urlService: UrlService,
        private usanaCookieService: UsanaCookieService
    ) {
        super(loggerService);
    }

    isPersonalAuthCountry(country: string): boolean {
        return PersonalAuthService.PERSONAL_AUTH_COUNTRIES.includes(country);
    }

    getPersonalAuthRequest(authMethod: string): Observable<IRestResponse> {
        const personalAuthRequest: any = {
            "returnUrl": this.urlService.buildCartUrl("/pa/response"),
            "errorUrl": this.urlService.buildCartUrl("/pa/response"),
            "authType": authMethod
        };

        return this.http.post(this.urlService.buildCartUrl("/pa"), personalAuthRequest).pipe(
            map((response: any) => response),
            catchError((error: any) => throwError(error))
        );
    }

    getNICEResponse(requestNumber: string): Observable<IRestResponse> {
        const params: { [param: string]: string } = {requestNumber: requestNumber};
        return this.http.get(this.urlService.buildCartUrl("/pa/niceResponse"), {params}).pipe(
            map((response: any) => response),
            catchError((error: any) => throwError(error))
        );
    }

    decodeAuthResponse(encodeData: string): Observable<any> {
        return this.http.post(this.urlService.buildCartUrl("/pa/decode"), {encodedData: encodeData}).pipe(
            map((response: any) => response),
            catchError((error: any) => throwError(error))
        );
    }

    getPersonalAuthResponse(): PersonalAuthResponse {
        return Util.parseJson(this.usanaCookieService.getCookie(Constants.ENROLL_PERSONAL_AUTH));
    }

    hasPersonalAuthResponse(): boolean {
        return !!this.getPersonalAuthResponse();
    }

    getPersonalRequestNumber(): string {
        return this.usanaCookieService.getCookie(Constants.ENROLL_PERSONAL_REQ_NUM);
    }

    hasPersonalAuthRequestNumber(): boolean {
        return !!this.getPersonalRequestNumber();
    }

    initPersonalAuthResponse(): Observable<PersonalAuthResponse> {
        if (this.hasPersonalAuthRequestNumber() && !this.hasPersonalAuthResponse()) {
            return this.getNICEResponse(this.getPersonalRequestNumber())
                .pipe(map((restResponse: IRestResponse) => {
                    if (restResponse.success) {
                        this.storePersonalAuthResponse(restResponse.message);
                        return this.getPersonalAuthResponse();
                    }
                }));
        }
        return of(this.getPersonalAuthResponse());
    }

    storePersonalAuthResponse(personalAuthResponse: PersonalAuthResponse) {
        this.log.debug(personalAuthResponse);
        // we don't need to store the entire response, only what we will be using from it. this will also help in keeping cookie size lower.
        const liteResponse = {
            auth_type: personalAuthResponse.auth_type,
            customerName: personalAuthResponse.customerName,
            birthDate: personalAuthResponse.birthDate,
            gender: personalAuthResponse.gender,
            mobileNumber: personalAuthResponse.mobileNumber
        } as PersonalAuthResponse;
        this.usanaCookieService.setCookie(Constants.ENROLL_PERSONAL_AUTH, JSON.stringify(liteResponse), null);
    }

    storePersonalAuthRequestNumber(requestNumber: string) {
        this.usanaCookieService.setCookie(Constants.ENROLL_PERSONAL_REQ_NUM, requestNumber, null);
    }

    removePersonalAuthCookies() {
        this.usanaCookieService.removeCookie(Constants.ENROLL_PERSONAL_AUTH);
        this.usanaCookieService.removeCookie(Constants.ENROLL_PERSONAL_REQ_NUM);
    }

}
